import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const inputGroupVariants = cva("relative flex w-full items-center", {
  variants: {
    variant: {
      default: "",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

interface InputGroupProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof inputGroupVariants> {}

const InputGroup = React.forwardRef<HTMLDivElement, InputGroupProps>(
  ({ className, variant, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(inputGroupVariants({ variant }), className)}
        {...props}
      />
    );
  }
);
InputGroup.displayName = "InputGroup";

const InputGroupInput = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>(({ className, ...props }, ref) => {
  return (
    <input
      ref={ref}
      className={cn(
        "flex h-11 w-full rounded-md border border-gray-300 bg-gray-50 px-4 py-2 text-base transition-colors relative z-0",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-prime-700 focus-visible:ring-offset-2 focus-visible:z-10",
        "disabled:cursor-not-allowed disabled:opacity-50",
        "placeholder:text-gray-400",
        className
      )}
      {...props}
    />
  );
});
InputGroupInput.displayName = "InputGroupInput";

interface InputGroupAddonProps extends React.HTMLAttributes<HTMLDivElement> {
  align?: "start" | "end" | "inline-start" | "inline-end";
  asChild?: boolean;
}

const InputGroupAddon = React.forwardRef<HTMLDivElement, InputGroupAddonProps>(
  ({ className, align = "end", asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "div";

    const isInline = align === "inline-start" || align === "inline-end";

    if (isInline) {
      return (
        <Comp
          ref={ref}
          className={cn(
            "pointer-events-none absolute flex items-center text-gray-500",
            align === "inline-start" && "left-3",
            align === "inline-end" && "right-3",
            className
          )}
          {...props}
        >
          {children}
        </Comp>
      );
    }

    return (
      <Comp
        ref={ref}
        className={cn(
          "flex items-center whitespace-nowrap border border-gray-300 bg-gray-100 px-3 text-sm text-gray-600",
          align === "start" && "rounded-l-md border-r-0",
          align === "end" && "rounded-r-md border-l-0",
          className
        )}
        {...props}
      >
        {children}
      </Comp>
    );
  }
);
InputGroupAddon.displayName = "InputGroupAddon";

export { InputGroup, InputGroupInput, InputGroupAddon };
